import { Injectable, Injector, signal, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { 
  signIn, 
  signOut, 
  getCurrentUser, 
  fetchAuthSession, 
  confirmSignIn,
  resetPassword,
  confirmResetPassword,
  signInWithRedirect,
  type SignInOutput,
  type ConfirmSignInOutput 
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { AuthUser } from '../models/auth.model';
import { tenant } from '../../../tenants/tenant';
import { environment } from '../../../environments/environment';
import { reconfigureAmplifyForNative, reconfigureAmplifyForSaml } from '../config/amplify.config';
import { firstValueFrom, from, Observable, of, switchMap } from 'rxjs';
import { UserService } from './user-service';
import { SubTenantService } from './subtenant-service';
import { IUser, User } from '../models/user.model';
//import { jwtDecode } from 'jwt-decode';
//import { JwtPayload } from './jwt-payload';

const AUTH_TYPE_STORAGE_KEY = 'app_auth_type';
const SUBTENANT_TOKEN_KEY = 'subtenant_token';//Presa SubtenantService
const USER_ALIGNED_KEY = 'saml_user_aligned';
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly injector = inject(Injector);
  private get translate(): TranslateService {
    return this.injector.get(TranslateService);
  }
  userService = inject(UserService);
  subTenantService = inject(SubTenantService);
  user = signal<AuthUser | null>(null);
  isLoading = signal<boolean>(false);
  error = signal<string | null>(null);
  challengeUser = signal<any>(null);

  readonly isAuthChecked = signal<boolean | null>(null);

  private isLoggingOut = false;

  private authCheckResolver: ((authenticated: boolean) => void) | null = null;
  private readonly authCheckPromise: Promise<boolean>;
  private TOKEN_KEY = 'appToken'; 

  private get authType(): 'saml' | 'native' {
    if (!tenant.samlEnabled) return 'native';
    return (localStorage.getItem(AUTH_TYPE_STORAGE_KEY) as 'saml' | 'native') || 'saml';
  }

  private set authType(value: 'saml' | 'native') {
    localStorage.setItem(AUTH_TYPE_STORAGE_KEY, value);
  }

  constructor() {
    this.authCheckPromise = new Promise<boolean>(resolve => {
      this.authCheckResolver = resolve;
    });
    this.setupHubListener();
    this.checkExistingSession();
  }

  async getAppToken(): Promise<string | null> { 
    return Promise.resolve(sessionStorage.getItem(SUBTENANT_TOKEN_KEY));
    //return Promise.resolve(localStorage.getItem(this.TOKEN_KEY));
  }
  
  setAppToken(token: string) { 

    //localStorage.setItem(this.TOKEN_KEY, token); 
  }

  private alignmentInFlight: Promise<boolean> | null = null;
  private profileInFlight: Promise<void> | null = null;
  private profileLoaded = false;
  private refreshInFlight: Promise<string | null> | null = null;

  async refreshAppToken(): Promise<string | null> {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    this.refreshInFlight = this.doRefreshAppToken();
    try {
      return await this.refreshInFlight;
    } finally {
      this.refreshInFlight = null;
    }
  }

  private async doRefreshAppToken(forceRefresh: boolean = false): Promise<string | null> {
    try {
      const session = await fetchAuthSession({ forceRefresh: forceRefresh });
      const cognitoToken = session.tokens?.idToken?.toString();
      if (!cognitoToken) return null;

      let subtenantId = this.subTenantService.getSelectedSubtenantId();
      if (!subtenantId) {
        const currentToken = await this.getAppToken();
        subtenantId = currentToken ? this.extractSubtenantId(currentToken) : null;
      }
      if (!subtenantId) return null;

      const newToken = await firstValueFrom(this.subTenantService.selectSubtenant(subtenantId));
      return newToken ?? null;
    } catch (err) {
      console.error('Refresh failed', err);
      return null;
    }
  }

  private extractSubtenantId(token: string): string | null {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return typeof payload?.subtenantId === 'string' ? payload.subtenantId : null;
    } catch {
      return null;
    }
  }

  getValidToken(): Observable<string|null> {
    return from(this.getAppToken()).pipe(
      switchMap(token => token
        ? of(token)
        : from(this.getAccessToken())
      )
    );
  }


  private setupHubListener(): void {
    Hub.listen('auth', ({ payload }) => {
      console.log('[AuthService] Hub auth event:', payload.event);
      switch (payload.event) {
        case 'signInWithRedirect':
          this.loadCurrentUser()
            .then(() => this.resolveAuthCheck(true))
            .catch(() => this.resolveAuthCheck(false));
          break;
        case 'signInWithRedirect_failure':
          console.error('[AuthService] SAML sign-in redirect failed');
          this.resolveAuthCheck(false);
          break;
        case 'signedIn':
          this.loadCurrentUser()
            .then(() => this.resolveAuthCheck(true))
            .catch(() => this.resolveAuthCheck(false));
          break;
        case 'signedOut':
          if (!this.isLoggingOut) {
            this.user.set(null);
            sessionStorage.removeItem('auth_user');
            sessionStorage.removeItem(USER_ALIGNED_KEY);
            this.isAuthChecked.set(false);
          }
          break;
      }
    });
  }

  private async checkExistingSession(): Promise<void> {
    try {
      await getCurrentUser();
      await this.loadCurrentUser();
      this.resolveAuthCheck(true);
    } catch {
      this.user.set(null);
      sessionStorage.removeItem('auth_user');

      const params = new URLSearchParams(window.location.search);
      if (tenant.samlEnabled && (params.has('code') || params.has('error'))) {
        return;
      }
      this.resolveAuthCheck(false);
    }
  }

  private resolveAuthCheck(authenticated: boolean): void {
    this.isAuthChecked.set(authenticated);
    if (this.authCheckResolver) {
      this.authCheckResolver(authenticated);
      this.authCheckResolver = null;
    }
  }

  waitForAuthCheck(): Promise<boolean> {
    const current = this.isAuthChecked();
    if (current !== null) return Promise.resolve(current);
    return this.authCheckPromise;
  }

  async loginWithSaml(): Promise<void> {
    try {
      this.authType = 'saml';
      reconfigureAmplifyForSaml();
      await signInWithRedirect({
        provider: { custom: environment.samlProviderName },
      });
    } catch (error: any) {
      console.error('SAML login error:', error);
      throw error;
    }
  }

  async login(username: string, password: string): Promise<SignInOutput> {
    this.isLoading.set(true);
    this.error.set(null);

    try {
      if (tenant.samlEnabled) {
        this.authType = 'native';
        reconfigureAmplifyForNative();
      }

      const isAlreadySignedIn = await this.isAuthenticated();
      
      if (isAlreadySignedIn) {
        await this.logout();
      }

      const output: SignInOutput = await signIn({
        username,
        password,
      });

      if (output.isSignedIn) {
        /*const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString(); 

        await this.userService.postSubtenant('subtenant-1').subscribe({
          next: (appToken) => {
             this.setAppToken(appToken.token);
          }
        });*/
        await this.loadCurrentUser();
      } else if (output.nextStep) {
        this.challengeUser.set(output);
        
        if (output.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
          return output;
        } else if (output.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE') {
          throw new Error(this.translate.instant('AUTH.ERR_SMS_MFA'));
        } else if (output.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
          throw new Error(this.translate.instant('AUTH.ERR_TOTP_MFA'));
        } else if (output.nextStep.signInStep === 'CONFIRM_SIGN_UP') {
          throw new Error(this.translate.instant('AUTH.ERR_EMAIL_VERIFY'));
        }
      }

      return output;
    } catch (error: any) {
      const errorMessage = this.getErrorMessage(error);
      this.error.set(errorMessage);
      throw error;
    } finally {
      this.isLoading.set(false);
    }
  }

  async completeNewPasswordChallenge(newPassword: string): Promise<void> {
    this.isLoading.set(true);
    this.error.set(null);

    try {
      const output: ConfirmSignInOutput = await confirmSignIn({
        challengeResponse: newPassword
      });

      if (output.isSignedIn) {
        await this.loadCurrentUser();
        this.challengeUser.set(null);
      } else {
        throw new Error(this.translate.instant('AUTH.ERR_PASSWORD_CHALLENGE'));
      }
    } catch (error: any) {
      const errorMessage = this.getErrorMessage(error);
      this.error.set(errorMessage);
      throw error;
    } finally {
      this.isLoading.set(false);
    }
  }

  async forgotPassword(username: string): Promise<void> {
    this.isLoading.set(true);
    this.error.set(null);

    try {
      const output = await resetPassword({
        username
      });

      if (output.nextStep.resetPasswordStep === 'CONFIRM_RESET_PASSWORD_WITH_CODE') {
        return;
      }
    } catch (error: any) {
      const errorMessage = this.getErrorMessage(error);
      this.error.set(errorMessage);
      throw error;
    } finally {
      this.isLoading.set(false);
    }
  }

  async forgotPasswordSubmit(username: string, code: string, newPassword: string): Promise<void> {
    this.isLoading.set(true);
    this.error.set(null);

    try {
      await confirmResetPassword({
        username,
        confirmationCode: code,
        newPassword
      });
    } catch (error: any) {
      const errorMessage = this.getErrorMessage(error);
      this.error.set(errorMessage);
      throw error;
    } finally {
      this.isLoading.set(false);
    }
  }

  async logout(): Promise<void> {
    this.isLoggingOut = true;
    this.profileLoaded = false;
    this.userService.clearUserCache();
    localStorage.removeItem(this.TOKEN_KEY);
    sessionStorage.removeItem(USER_ALIGNED_KEY);
    this.subTenantService.clearSubtenant();

    try {
      const currentAuthType = this.authType;

      if (currentAuthType === 'saml' && tenant.samlEnabled) {
        reconfigureAmplifyForSaml();
        await signOut({ global: true });
      } else {
        if (tenant.samlEnabled) {
          localStorage.removeItem(AUTH_TYPE_STORAGE_KEY);
        }
        await signOut({ global: true });
        this.user.set(null);
        sessionStorage.removeItem('auth_user');
        this.isAuthChecked.set(false);

        const redirectUrl = environment.amplify.oauth.redirectSignOut;
        window.location.assign(redirectUrl);
      }
    } catch (error: any) {
      console.error('Error signing out:', error);
      this.user.set(null);
      sessionStorage.removeItem('auth_user');
      this.isAuthChecked.set(false);
      window.location.assign('/');
    } finally {
      this.isLoggingOut = false;
    }
  }

  async getCurrentUser(): Promise<AuthUser | null> {
    if (this.user()) {
      return this.user();
    }

    try {
      await this.loadCurrentUser();
      return this.user();
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  }

  private async loadCurrentUser(): Promise<void> {
    try {
      const amplifyUser = await getCurrentUser();
      const session = await fetchAuthSession();

      const idToken = session.tokens?.idToken;
      const attributes = idToken?.payload || {};

      const authUser: AuthUser = {
        id: amplifyUser.userId,
        cognitoUserName: amplifyUser.username,
        email: (attributes['email'] as string) || (attributes['cognito:username'] as string),
        name: (attributes['name'] as string) || (attributes['given_name'] as string) || null,
        surname: (attributes['family_name'] as string) || (attributes['surname'] as string) || null,
        account: amplifyUser.username,
        status: attributes['cognito:user_status'] as string || null,
        group: this.mapGroupFromAttributes(attributes),
        userSaml: attributes['identities'] ? true : false,
      };

      this.profileLoaded = false;
      this.user.set(authUser);
      sessionStorage.setItem('auth_user', JSON.stringify(authUser));
    } catch (error) {
      console.error('Error loading current user:', error);
      this.user.set(null);
      sessionStorage.removeItem('auth_user');
      throw error;
    }
  }

  mergeUserProfile(dbUser: IUser | null | undefined): void {
    const currentUser = this.user() ?? this.getUser();

    if (!dbUser || !currentUser || (currentUser.name && currentUser.surname)) {
      return;
    }

    const mergedUser: AuthUser = {
      ...currentUser,
      name: dbUser.name ?? currentUser.name,
      surname: dbUser.surname ?? currentUser.surname,
    };

    this.user.set(mergedUser);
    sessionStorage.setItem('auth_user', JSON.stringify(mergedUser));
  }

  async ensureUserProfile(): Promise<void> {
    const currentUser = this.user() ?? this.getUser();

    if (!currentUser || this.profileLoaded || (currentUser.name && currentUser.surname)) {
      return;
    }

    if (!this.subTenantService.subtenantsChecked()) {
      return;
    }

    if (this.profileInFlight) {
      return this.profileInFlight;
    }

    this.profileInFlight = this.doEnsureUserProfile();
    try {
      await this.profileInFlight;
    } finally {
      this.profileInFlight = null;
    }
  }

  private async doEnsureUserProfile(): Promise<void> {
    try {
      const dbUser = await firstValueFrom(this.userService.getUser());
      this.mergeUserProfile(dbUser);
      this.profileLoaded = true;
    } catch (error) {
      console.error('[AuthService] Unable to load the user profile:', error);
    }
  }

  async ensureUserAlignment(): Promise<boolean> {
    if (!tenant.samlEnabled) {
      return true;
    }

    if (sessionStorage.getItem(USER_ALIGNED_KEY) === 'true') {
      return true;
    }

    if (this.alignmentInFlight) {
      return this.alignmentInFlight;
    }

    this.alignmentInFlight = this.doEnsureUserAlignment();
    try {
      return await this.alignmentInFlight;
    } finally {
      this.alignmentInFlight = null;
    }
  }

  private async doEnsureUserAlignment(): Promise<boolean> {
    const currentUser = this.user() ?? this.getUser();
    const email = currentUser?.email;
    const cognitoSub = currentUser?.id;

    if (!email || !cognitoSub) {
      return true;
    }

    try {
      const dbUser = await firstValueFrom(
        this.userService.getUserByEmail(new User({ email }))
      );

      if (!dbUser) {
        console.error('[AuthService] Authenticated user is not enabled in the DB.');
        return false;
      }

      this.mergeUserProfile(dbUser);

      if (dbUser.id !== cognitoSub) {
        console.warn('[AuthService] User ids are not aligned. Calling align-user API.');
        const userToAlign = new User({
          ...dbUser,
          id: cognitoSub,
          tempId: dbUser.id,
          cognitoUserName: currentUser?.cognitoUserName ?? undefined,
        });
        await firstValueFrom(this.userService.alignUser(userToAlign));
        await this.doRefreshAppToken(true);
        console.log('[AuthService] User alignment completed.');
      }

      sessionStorage.setItem(USER_ALIGNED_KEY, 'true');
      return true;
    } catch (error) {
      console.error('[AuthService] User alignment failed:', error);
      return false;
    }
  }

  getUser(): AuthUser | null {
    if (!this.user()) {
      const user = sessionStorage.getItem('auth_user');
      if (user) {
        try {
          const userData = JSON.parse(user);
          this.user.set(userData);
        } catch (error) {
          console.error('Error parsing stored user:', error);
          sessionStorage.removeItem('auth_user');
        }
      }
    }
    return this.user() || null;
  }

  async isAuthenticated(): Promise<boolean> {
    try {
      await getCurrentUser();
      return true;
    } catch {
      return false;
    }
  }


  async getAccessToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString() || null;
    } catch (error) {
      console.error('Error getting access token:', error);
      return null;
    }
  }


  async getIdToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() || null;
    } catch (error) {
      console.error('Error getting ID token:', error);
      return null;
    }
  }


  async getAuthSession() {
    try {
      return await fetchAuthSession();
    } catch (error) {
      console.error('Error getting auth session:', error);
      return null;
    }
  }


  private mapGroupFromAttributes(attributes: any): AuthUser['group'] {
    const groups = attributes['cognito:groups'] as string[] || [];
    
    if (groups.includes('SYS_ADMIN')) {
      return 'SYS_ADMIN' as any;
    } else if (groups.includes('ADMIN')) {
      return 'ADMIN' as any;
    } else if (groups.includes('USER')) {
      return 'USER' as any;
    }
    
    return null;
  }


  private getErrorMessage(error: any): string {
    if (error.name === 'NotAuthorizedException') {
      return this.translate.instant('AUTH.ERR_INVALID_CREDENTIALS');
    } else if (error.name === 'UserNotConfirmedException') {
      return this.translate.instant('AUTH.ERR_EMAIL_VERIFY');
    } else if (error.name === 'UserNotFoundException') {
      return this.translate.instant('AUTH.ERR_USER_NOT_FOUND');
    } else if (error.name === 'TooManyRequestsException') {
      return this.translate.instant('AUTH.ERR_TOO_MANY_ATTEMPTS');
    } else if (error.name === 'LimitExceededException') {
      return this.translate.instant('AUTH.ERR_TOO_MANY_ATTEMPTS');
    } else if (error.name === 'CodeMismatchException') {
      return this.translate.instant('AUTH.ERR_INVALID_CODE');
    } else if (error.name === 'ExpiredCodeException') {
      return this.translate.instant('AUTH.ERR_CODE_EXPIRED');
    } else if (error.name === 'InvalidPasswordException') {
      return this.translate.instant('AUTH.ERR_PASSWORD_REQUIREMENTS');
    } else if (error.name === 'InvalidParameterException') {
      return this.translate.instant('AUTH.ERR_INVALID_PARAMETER');
    } else if (error.message?.includes('already a signed in user') || error.message?.includes('already signed in')) {
      return this.translate.instant('AUTH.ERR_ALREADY_SIGNED_IN');
    } else if (error.message) {
      return error.message;
    }
    return this.translate.instant('AUTH.ERR_SIGN_IN');
  }

  isTokenExpired(token: string, offsetSeconds = 30): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));

      const expiry = payload.exp * 1000; // ms
      const now = Date.now();

      // refresh a bit before actual expiration
      return expiry - offsetSeconds * 1000 < now;

    } catch {
      return true; // invalid token → treat as expired
    }
  }

}
